import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { useFleetData } from "./FleetDataContext";
import { supabase } from "./supabaseClient";
import Nav from "./Nav";

const ACCENT = "#fcd657";

/* ─── Star field background ──────────────────────────────────────────────── */
function StarField() {
  const canvasRef = useRef(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    let animId;
    const stars = Array.from({ length: 200 }, () => ({
      x: Math.random(), y: Math.random(),
      r: Math.random() * 1.5 + 0.2, a: Math.random(),
      speed: Math.random() * 0.003 + 0.001,
    }));
    const resize = () => { canvas.width = window.innerWidth; canvas.height = window.innerHeight; };
    resize();
    window.addEventListener("resize", resize);
    const draw = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      stars.forEach(s => {
        s.a += s.speed; if (s.a > 1) s.a = 0;
        const alpha = Math.sin(s.a * Math.PI) * 0.7 + 0.15;
        ctx.beginPath();
        ctx.arc(s.x * canvas.width, s.y * canvas.height, s.r, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255,255,255,${alpha})`;
        ctx.fill();
      });
      animId = requestAnimationFrame(draw);
    };
    draw();
    return () => { cancelAnimationFrame(animId); window.removeEventListener("resize", resize); };
  }, []);
  return <canvas ref={canvasRef} style={{ position: "fixed", top: 0, left: 0, width: "100%", height: "100vh", pointerEvents: "none", zIndex: 0 }} />;
}

/* ─── Resources Page — Category Card Grid ────────────────────────────────── */
export default function ResourcesPage() {
  const { user, profile } = useAuth();
  const { profiles: preloadedProfiles } = useFleetData();
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [resourceCounts, setResourceCounts] = useState({});
  const [loading, setLoading] = useState(true);

  const isSuperAdmin = profile?.perm_super_admin === true || profile?.admin_tier === "super_admin";

  useEffect(() => {
    loadCategories();
  }, []);

  async function loadCategories() {
    setLoading(true);
    try {
      const { data: cats } = await supabase.from("resource_categories").select("*").order("sort_order", { ascending: true });
      setCategories(cats || []);

      // get resource counts per category
      const { data: resources } = await supabase.from("resources").select("id, category_id");
      const counts = {};
      (resources || []).forEach(r => { counts[r.category_id] = (counts[r.category_id] || 0) + 1; });
      setResourceCounts(counts);
    } catch (e) { /* tables may not exist yet */ }
    setLoading(false);
  }

  // Resolve creator name
  function getCreatorName(userId) {
    const p = (preloadedProfiles || []).find(pr => pr.id === userId);
    return p?.discord_username || p?.discord_display_name || "Unknown";
  }

  return (
    <div style={{ fontFamily: "'Rajdhani', 'Orbitron', sans-serif", background: "#05070f", color: "#e8eaf6", minHeight: "100vh" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Rajdhani:wght@300;400;500;600;700&family=Orbitron:wght@400;600;700;900&family=Space+Mono&display=swap'); * { box-sizing: border-box; margin: 0; padding: 0; } ::selection { background: #fcd657; color: #05070f; } a { text-decoration: none; color: inherit; } .nav-link { font-family: 'Orbitron', sans-serif; font-size: 11px; letter-spacing: 2.5px; text-transform: uppercase; color: rgba(232,234,246,0.55); transition: color 0.2s; padding: 4px 0; position: relative; } .nav-link::after { content: ''; position: absolute; bottom: 0; left: 0; width: 0; height: 1px; background: #fcd657; transition: width 0.3s ease; } .nav-link:hover { color: #fcd657; } .nav-link:hover::after { width: 100%; } .scanline { position: fixed; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none; z-index: 9999; background: repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.03) 2px, rgba(0,0,0,0.03) 4px); } ::-webkit-scrollbar { width: 4px; } ::-webkit-scrollbar-track { background: rgba(255,255,255,0.03); } ::-webkit-scrollbar-thumb { background: rgba(252,214,87,0.3); }`}</style>
      <div className="scanline" />
      <StarField />
      <Nav accentColor={ACCENT} />

      <div style={{ position: "relative", zIndex: 1, paddingTop: "calc(64px + env(safe-area-inset-top, 0px) + 40px)", paddingBottom: 80, paddingLeft: "clamp(16px, 4vw, 60px)", paddingRight: "clamp(16px, 4vw, 60px)", maxWidth: 1200, margin: "0 auto" }}>

        {/* Header */}
        <div style={{ marginBottom: 48 }}>
          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 8, letterSpacing: 4, color: `${ACCENT}88`, textTransform: "uppercase", marginBottom: 8 }}>Starkeeper Industries</div>
          <h1 style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 900, fontSize: "clamp(22px, 4vw, 34px)", letterSpacing: 3, color: "#e8eaf6", marginBottom: 8 }}>General Resources</h1>
          <div style={{ width: 60, height: 2, background: ACCENT, marginBottom: 16 }} />
          <p style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 15, color: "rgba(232,234,246,0.45)", lineHeight: 1.6, maxWidth: 600 }}>
            Shared knowledge base and reference materials for all members.
          </p>

          {isSuperAdmin && (
            <Link to="/admin" onClick={() => { /* they'll navigate to admin resources section */ }}
              style={{ display: "inline-block", marginTop: 16, fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, textTransform: "uppercase", padding: "8px 18px", background: `${ACCENT}15`, border: `1px solid ${ACCENT}44`, color: ACCENT, textDecoration: "none", transition: "all 0.2s" }}>
              Manage Resources in Admin
            </Link>
          )}
        </div>

        {/* Loading */}
        {loading && (
          <div style={{ textAlign: "center", padding: 60, fontFamily: "'Orbitron', sans-serif", fontSize: 12, letterSpacing: 3, color: `${ACCENT}66` }}>
            LOADING RESOURCES...
          </div>
        )}

        {/* Featured trackers — always shown above categories */}
        {!loading && (
          <div style={{ marginBottom: 32 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 14 }}>
              <div style={{ width: 24, height: 2, background: ACCENT }} />
              <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 4, color: ACCENT, textTransform: "uppercase" }}>Trackers</span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(min(320px, 100%), 1fr))", gap: 16 }}>
              <Link to="/resources/blueprints"
                style={{
                  display: "block", padding: "clamp(20px, 3vw, 28px)", textDecoration: "none",
                  border: `1px solid ${ACCENT}55`, background: `${ACCENT}0c`,
                  clipPath: "polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 0 100%)",
                  position: "relative", transition: "all 0.25s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = ACCENT; e.currentTarget.style.background = `${ACCENT}16`; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = `${ACCENT}55`; e.currentTarget.style.background = `${ACCENT}0c`; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: `${ACCENT}cc`, textTransform: "uppercase", marginBottom: 6 }}>Org Tracker</div>
                <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: "clamp(16px, 2.5vw, 20px)", color: "#e8eaf6", letterSpacing: 1, marginBottom: 6 }}>
                  Blueprint Tracker
                </div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.55)", lineHeight: 1.5 }}>
                  See which armor, weapon, and gear blueprints are owned across the org — search by category, filter by family, and check who has what.
                </div>
                <div style={{ position: "absolute", top: 14, right: 22, fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, color: ACCENT, textTransform: "uppercase" }}>Open &rarr;</div>
              </Link>

              <Link to="/resources/items"
                style={{
                  display: "block", padding: "clamp(20px, 3vw, 28px)", textDecoration: "none",
                  border: "1px solid rgba(79,195,247,0.4)", background: "rgba(79,195,247,0.05)",
                  clipPath: "polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 0 100%)",
                  position: "relative", transition: "all 0.25s",
                }}
                onMouseEnter={e => { e.currentTarget.style.borderColor = "#4fc3f7"; e.currentTarget.style.background = "rgba(79,195,247,0.12)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(79,195,247,0.4)"; e.currentTarget.style.background = "rgba(79,195,247,0.05)"; e.currentTarget.style.transform = "translateY(0)"; }}
              >
                <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, letterSpacing: 3, color: "rgba(79,195,247,0.8)", textTransform: "uppercase", marginBottom: 6 }}>Catalog</div>
                <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: "clamp(16px, 2.5vw, 20px)", color: "#e8eaf6", letterSpacing: 1, marginBottom: 6 }}>
                  Star Citizen Items
                </div>
                <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 14, color: "rgba(232,234,246,0.55)", lineHeight: 1.5 }}>
                  Searchable catalog of ship components, weapons, and gear — every item with a sharable deep link, ready to reference in event briefings and loadouts.
                </div>
                <div style={{ position: "absolute", top: 14, right: 22, fontFamily: "'Orbitron', sans-serif", fontSize: 9, letterSpacing: 2, color: "#4fc3f7", textTransform: "uppercase" }}>Open &rarr;</div>
              </Link>
            </div>
          </div>
        )}

        {/* Empty state */}
        {!loading && categories.length === 0 && (
          <div style={{ textAlign: "center", padding: 80, border: "1px solid rgba(255,255,255,0.06)", background: "rgba(255,255,255,0.02)" }}>
            <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 32, color: "rgba(255,255,255,0.08)", marginBottom: 16 }}>◇</div>
            <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 16, color: "rgba(232,234,246,0.3)" }}>No resource categories yet.</div>
            {isSuperAdmin && (
              <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 10, color: "rgba(232,234,246,0.2)", marginTop: 8 }}>Head to Admin to create categories.</div>
            )}
          </div>
        )}

        {/* Category Card Grid */}
        {!loading && categories.length > 0 && (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(clamp(240px, 30vw, 320px), 1fr))", gap: 20 }}>
            {categories.map(cat => {
              const count = resourceCounts[cat.id] || 0;
              return (
                <div key={cat.id}
                  onClick={() => navigate(`/resources/${cat.id}`)}
                  style={{
                    padding: "clamp(20px, 3vw, 30px)", cursor: "pointer", transition: "all 0.25s",
                    border: "1px solid rgba(252,214,87,0.18)", background: "rgba(252,214,87,0.03)",
                    clipPath: "polygon(0 0, calc(100% - 16px) 0, 100% 16px, 100% 100%, 0 100%)",
                    position: "relative",
                  }}
                  onMouseEnter={e => { e.currentTarget.style.borderColor = "rgba(252,214,87,0.5)"; e.currentTarget.style.background = "rgba(252,214,87,0.07)"; e.currentTarget.style.transform = "translateY(-2px)"; }}
                  onMouseLeave={e => { e.currentTarget.style.borderColor = "rgba(252,214,87,0.18)"; e.currentTarget.style.background = "rgba(252,214,87,0.03)"; e.currentTarget.style.transform = "translateY(0)"; }}
                >
                  {/* Sort badge */}
                  <div style={{ position: "absolute", top: 12, right: 20, fontFamily: "'Space Mono', monospace", fontSize: 8, color: "rgba(252,214,87,0.25)" }}>
                    #{cat.sort_order}
                  </div>

                  {/* Icon */}
                  <div style={{ fontFamily: "'Space Mono', monospace", fontSize: 28, color: ACCENT, marginBottom: 14, opacity: 0.7 }}>◇</div>

                  {/* Title */}
                  <div style={{ fontFamily: "'Orbitron', sans-serif", fontWeight: 700, fontSize: "clamp(14px, 2.5vw, 18px)", color: "#e8eaf6", marginBottom: 6, letterSpacing: 1 }}>
                    {cat.name}
                  </div>

                  {/* Description */}
                  {cat.description && (
                    <div style={{ fontFamily: "'Rajdhani', sans-serif", fontSize: 13, color: "rgba(232,234,246,0.4)", lineHeight: 1.5, marginBottom: 12, display: "-webkit-box", WebkitLineClamp: 2, WebkitBoxOrient: "vertical", overflow: "hidden" }}>
                      {cat.description}
                    </div>
                  )}

                  {/* Footer */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 12 }}>
                    <span style={{ fontFamily: "'Space Mono', monospace", fontSize: 9, color: "rgba(232,234,246,0.25)", letterSpacing: 2 }}>
                      {count} {count === 1 ? "resource" : "resources"}
                    </span>
                    <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 8, letterSpacing: 2, color: ACCENT, textTransform: "uppercase" }}>
                      View &rarr;
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
